import {
  enrichmentAuditDetail,
  enrichmentAuditLabel,
  isEnrichmentAuditEventVisible,
} from "./enrichmentReviewAudit";

export function resolveAuditUserName(userId, userDirectory) {
  const id = String(userId || "").trim();
  if (!id) return "";
  const map = userDirectory && typeof userDirectory === "object" ? userDirectory : {};
  return map[id] || "";
}

/** Human-readable label for one relationship audit diff row. */
export function formatRelationshipAuditLine(rel, formatLabel = (v) => String(v || "")) {
  if (!rel || typeof rel !== "object") return "Relationship";
  const typeLabel = formatLabel(rel.type || "relationship");
  const neighbor =
    rel.other_entity_name ||
    rel.other_entity_id ||
    "unknown entity";
  const direction = String(rel.direction || "").toLowerCase();
  if (direction === "incoming") {
    return `${typeLabel} from ${neighbor}`;
  }
  if (direction === "outgoing") {
    return `${typeLabel} to ${neighbor}`;
  }
  return `${typeLabel} · ${neighbor}`;
}

export function entityAuditChangeTitle(entry) {
  if (!entry || typeof entry !== "object") return "Updated";
  if (entry.change_type === "create") return "Created";
  if (entry.change_type === "delete") return "Deleted";
  if (entry.change_type === "relationship_update") return "Relationships updated";
  if (!entry.item_type || entry.item_type === "audit_log") {
    const version = entry.version != null ? entry.version : "—";
    return `v${version} – ${entry.change_type || "updated"}`;
  }
  return entry.change_type || "Updated";
}

/** True when an entity_audit_log row has field/relationship/lifecycle detail worth showing. */
export function entityAuditEntryHasVisibleDetails(
  entry,
  normalizeAuditJson,
  isMeaningfulAuditField
) {
  if (!entry || typeof entry !== "object") return false;

  if (entry.change_type === "create" || entry.change_type === "delete") {
    return true;
  }

  if (entry.item_type === "lifecycle" && entry.lifecycle) {
    const L = entry.lifecycle;
    const meta = normalizeAuditJson(L.metadata) || {};
    const statusLine = [L.from_status, L.to_status].filter(Boolean).join(" → ");
    if (statusLine) return true;
    if (entry.change_type === "merge_entities") return true;
    if (meta.role === "merge_target_canonical") return true;
    if (meta.target_entity_id || meta.source_entity_id) return true;
    const metaKeys = Object.keys(meta).filter(
      (k) => !["role", "source_entity_id", "target_entity_id"].includes(k)
    );
    if (metaKeys.length > 0) return true;
    return false;
  }

  const attrDiff = normalizeAuditJson(entry.attributes_diff);
  const relDiff = normalizeAuditJson(entry.relationships_diff);
  const changedAttrsRaw = attrDiff.changed || {};
  const changedAttrs = Object.entries(changedAttrsRaw).filter(([key]) =>
    isMeaningfulAuditField(key)
  );
  const addedAttrs = (attrDiff.added || []).filter((key) =>
    isMeaningfulAuditField(key)
  );
  const removedAttrs = (attrDiff.removed || []).filter((key) =>
    isMeaningfulAuditField(key)
  );
  if (changedAttrs.length > 0 || addedAttrs.length > 0 || removedAttrs.length > 0) {
    return true;
  }
  if (
    relDiff.added?.length ||
    relDiff.removed?.length ||
    relDiff.changed?.length
  ) {
    return true;
  }
  return false;
}

export function enrichmentAuditEventTimestamp(ev) {
  return String(ev?.at || "").trim();
}

export function entityAuditEntryTimestamp(entry) {
  return String(entry?.changed_at || "").trim();
}

/** Merge entity audit rows and enrichment review events into one timeline (newest first). */
export function buildMergedAuditTimeline(
  entityEntries,
  enrichmentEntries,
  { normalizeAuditJson, isMeaningfulAuditField }
) {
  const items = [];

  (Array.isArray(entityEntries) ? entityEntries : []).forEach((entry) => {
    if (
      !entityAuditEntryHasVisibleDetails(
        entry,
        normalizeAuditJson,
        isMeaningfulAuditField
      )
    ) {
      return;
    }
    items.push({
      kind: "entity",
      at: entityAuditEntryTimestamp(entry),
      entry,
    });
  });

  (Array.isArray(enrichmentEntries) ? enrichmentEntries : []).forEach((ev) => {
    if (!ev || typeof ev !== "object") return;
    if (!isEnrichmentAuditEventVisible(ev)) return;
    items.push({
      kind: "enrichment",
      at: enrichmentAuditEventTimestamp(ev),
      event: ev,
    });
  });

  items.sort((a, b) => {
    const ta = Date.parse(a.at) || 0;
    const tb = Date.parse(b.at) || 0;
    return tb - ta;
  });

  return items;
}

export function collectAuditUserIds(entityEntries, enrichmentEntries) {
  const ids = new Set();
  (entityEntries || []).forEach((entry) => {
    const id = String(entry?.changed_by_user_id || "").trim();
    if (id) ids.add(id);
  });
  (enrichmentEntries || []).forEach((ev) => {
    const id = String(ev?.actor_user_id || "").trim();
    if (id) ids.add(id);
  });
  return [...ids];
}

export function enrichmentEventTitle(ev) {
  return enrichmentAuditLabel(ev);
}

export function enrichmentEventSubtitle(ev) {
  const detail = enrichmentAuditDetail(ev);
  if (!detail) return "";
  const summary = String(ev?.summary || "").trim();
  if (summary.includes(detail)) return "";
  return detail;
}
